import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
interface TradeActivity {
  type: 'buy' | 'sell';
  asset: string;
  amount: string;
  price: string;
  time: string;
  date: string;
  orderId?: string;
  leverage?: string;
  market?: string;
  orderType?: string;
  orderState?: string;
  reduceOnly?: boolean;
  timeInForce?: string;
}
interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: TradeActivity | null;
}
export function TransactionModal({
  isOpen,
  onClose,
  transaction
}: TransactionModalProps) {
  const [activeTab, setActiveTab] = useState<'terms' | 'executions' | 'trades'>('terms');
  if (!transaction) return null;
  const isBuy = transaction.type === 'buy';
  // Mock data for executions tab
  const executionsData = {
    quantityExecuted: transaction.amount,
    cost: '105.92015',
    averagePrice: transaction.price,
    fee: '0.42368',
    opened: `${transaction.date} ${transaction.time}`,
    closed: `${transaction.date} ${transaction.time}`
  };
  // Mock data for trades tab
  const tradesData = [{
    date: `12/12/25`,
    time: '4:18 PM',
    side: transaction.type,
    type: 'Market',
    price: transaction.price,
    volume: '0.00034516',
    cost: '31.68'
  }, {
    date: `12/12/25`,
    time: '4:18 PM',
    side: transaction.type,
    type: 'Market',
    price: transaction.price,
    volume: '0.000809',
    cost: '74.24'
  }];
  return <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl" data-id="element-676">
      {/* Header with asset info */}
      <div className="px-6 py-6 border-b border-border-subtle" data-id="element-677">
        <div className="flex items-center gap-3 mb-4" data-id="element-678">
          <div className="w-12 h-12 rounded-full bg-warning/20 flex items-center justify-center" data-id="element-679">
            <span className="text-2xl" data-id="element-680">₿</span>
          </div>
          <div data-id="element-681">
            <h2 className="text-xl font-semibold text-text-primary" data-id="element-682">
              {transaction.asset}/USD
            </h2>
          </div>
        </div>

        <div className={`text-lg font-medium ${isBuy ? 'text-success' : 'text-danger'}`} data-id="element-683">
          {isBuy ? 'Buy' : 'Sell'} {transaction.amount} {transaction.asset} @{' '}
          {isBuy ? 'Limit' : 'Market'} {transaction.price} USD
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 px-6 py-4 border-b border-border-subtle" data-id="element-684">
        <button onClick={() => setActiveTab('terms')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'terms' ? 'bg-background-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-secondary/50'}`} data-id="element-685">
          Terms
        </button>
        <button onClick={() => setActiveTab('executions')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'executions' ? 'bg-background-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-secondary/50'}`} data-id="element-686">
          Executions
        </button>
        <button onClick={() => setActiveTab('trades')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'trades' ? 'bg-background-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-secondary/50'}`} data-id="element-687">
          Trades
        </button>
      </div>

      {/* Content */}
      <div className="px-6 py-6" data-id="element-688">
        {activeTab === 'terms' && <div className="space-y-4" data-id="element-689">
            {/* Order state */}
            <div className="flex items-center justify-between py-3" data-id="element-690">
              <span className="text-sm text-text-secondary" data-id="element-691">Order state</span>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-success/10 text-success text-sm font-medium" data-id="element-692">
                Filled
              </div>
            </div>

            {/* Order type */}
            <div className="flex items-center justify-between py-3" data-id="element-693">
              <span className="text-sm text-text-secondary" data-id="element-694">Order type</span>
              <span className="text-base font-medium text-text-primary" data-id="element-695">
                {isBuy ? 'Buy' : 'Sell'} – Limit
              </span>
            </div>

            {/* Leverage */}
            <div className="flex items-center justify-between py-3" data-id="element-696">
              <span className="text-sm text-text-secondary" data-id="element-697">Leverage</span>
              <span className="text-base font-medium text-text-primary" data-id="element-698">
                10x
              </span>
            </div>

            {/* Market */}
            <div className="flex items-center justify-between py-3" data-id="element-699">
              <span className="text-sm text-text-secondary" data-id="element-700">Market</span>
              <span className="text-base font-medium text-text-primary" data-id="element-701">
                {transaction.asset}
                <span className="text-text-secondary" data-id="element-702">/USD</span>
              </span>
            </div>

            {/* Price */}
            <div className="flex items-center justify-between py-3" data-id="element-703">
              <span className="text-sm text-text-secondary" data-id="element-704">Price</span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-705">
                {transaction.price}{' '}
                <span className="text-text-secondary text-sm" data-id="element-706">USD</span>
              </span>
            </div>

            {/* Reduce only */}
            <div className="flex items-center justify-between py-3" data-id="element-707">
              <span className="text-sm text-text-secondary" data-id="element-708">Reduce only</span>
              <span className="text-base font-medium text-text-primary" data-id="element-709">
                No
              </span>
            </div>

            {/* Order ID */}
            <div className="flex items-center justify-between py-3 pt-6 border-t border-border-subtle" data-id="element-710">
              <span className="text-sm text-text-secondary" data-id="element-711">Order ID</span>
              <span className="text-sm font-mono text-text-primary" data-id="element-712">
                OWY5CU-M6VZX-UHUHZ4
              </span>
            </div>
          </div>}

        {activeTab === 'executions' && <div className="space-y-4" data-id="element-713">
            {/* Quantity executed */}
            <div className="flex items-center justify-between py-3" data-id="element-714">
              <span className="text-sm text-text-secondary" data-id="element-715">
                Quantity executed
              </span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-716">
                {executionsData.quantityExecuted}{' '}
                <span className="text-text-secondary text-sm" data-id="element-717">
                  {transaction.asset}
                </span>
              </span>
            </div>

            {/* Cost */}
            <div className="flex items-center justify-between py-3" data-id="element-718">
              <span className="text-sm text-text-secondary" data-id="element-719">Cost</span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-720">
                {executionsData.cost}{' '}
                <span className="text-text-secondary text-sm" data-id="element-721">USD</span>
              </span>
            </div>

            {/* Average price */}
            <div className="flex items-center justify-between py-3" data-id="element-722">
              <span className="text-sm text-text-secondary" data-id="element-723">Average price</span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-724">
                {executionsData.averagePrice}{' '}
                <span className="text-text-secondary text-sm" data-id="element-725">USD</span>
              </span>
            </div>

            {/* Opened */}
            <div className="flex items-center justify-between py-3" data-id="element-726">
              <span className="text-sm text-text-secondary" data-id="element-727">Opened</span>
              <span className="text-base font-medium text-text-primary" data-id="element-728">
                {executionsData.opened}
              </span>
            </div>

            {/* Fee */}
            <div className="flex items-center justify-between py-3" data-id="element-729">
              <span className="text-sm text-text-secondary" data-id="element-730">Fee</span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-731">
                {executionsData.fee}{' '}
                <span className="text-text-secondary text-sm" data-id="element-732">USD</span>
              </span>
            </div>

            {/* Closed */}
            <div className="flex items-center justify-between py-3" data-id="element-733">
              <span className="text-sm text-text-secondary" data-id="element-734">Closed</span>
              <span className="text-base font-medium text-text-primary" data-id="element-735">
                {executionsData.closed}
              </span>
            </div>
          </div>}

        {activeTab === 'trades' && <div className="overflow-x-auto -mx-6 px-6" data-id="element-736">
            <table className="w-full" data-id="element-737">
              <thead data-id="element-738">
                <tr className="border-b border-border-subtle" data-id="element-739">
                  <th className="text-left py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-740">
                    Date
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-741">
                    Side
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-742">
                    Type
                  </th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-743">
                    Price
                  </th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-744">
                    Volume
                  </th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-745">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-746">
                {tradesData.map((trade, idx) => <tr key={idx} className="border-b border-border-subtle last:border-0" data-id="element-747">
                    <td className="py-4 px-2" data-id="element-748">
                      <div className="text-sm text-text-primary" data-id="element-749">
                        {trade.date}
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-750">
                        {trade.time}
                      </div>
                    </td>
                    <td className="py-4 px-2" data-id="element-751">
                      <div className={`text-sm font-medium capitalize ${trade.side === 'buy' ? 'text-success' : 'text-danger'}`} data-id="element-752">
                        {trade.side}
                      </div>
                    </td>
                    <td className="py-4 px-2" data-id="element-753">
                      <div className="text-sm text-text-primary" data-id="element-754">
                        {trade.type}
                      </div>
                    </td>
                    <td className="py-4 px-2 text-right" data-id="element-755">
                      <div className="text-sm font-numbers text-text-primary" data-id="element-756">
                        {trade.price}{' '}
                        <span className="text-text-secondary text-xs" data-id="element-757">USD</span>
                      </div>
                    </td>
                    <td className="py-4 px-2 text-right" data-id="element-758">
                      <div className="text-sm font-numbers text-text-primary" data-id="element-759">
                        {trade.volume}{' '}
                        <span className="text-text-secondary text-xs" data-id="element-760">
                          {transaction.asset}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-2 text-right" data-id="element-761">
                      <div className="text-sm font-numbers text-text-primary" data-id="element-762">
                        {trade.cost}{' '}
                        <span className="text-text-secondary text-xs" data-id="element-763">USD</span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </Modal>;
}